"use client"

import { ScanFaceIcon } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authClient } from "@/lib/auth-client"
import { authErrorMessage } from "@/lib/auth-errors"

// `next`: already validated by the page (safeNext), e.g. an invitation link.
export function LoginForm({ next }: { next: string }) {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  function onSuccess() {
    router.push(next)
    router.refresh()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setError(null)
    setPending(true)

    const { error } = await authClient.signIn.email({
      email: String(form.get("email")),
      password: String(form.get("password")),
    })

    if (error) {
      setError(authErrorMessage(error))
      setPending(false)
      return
    }
    onSuccess()
  }

  async function handlePasskey() {
    setError(null)
    setPending(true)

    const { error } = await authClient.signIn.passkey()

    if (error) {
      // The fallback also covers the user cancelling the system dialog.
      setError(
        authErrorMessage(
          error,
          "Die Anmeldung mit Face ID hat nicht geklappt. Versuch es nochmal oder nimm dein Passwort."
        )
      )
      setPending(false)
      return
    }
    onSuccess()
  }

  return (
    <div className="flex flex-col gap-6">
      <Button
        variant="outline"
        size="lg"
        disabled={pending}
        onClick={handlePasskey}
      >
        <ScanFaceIcon className="size-6" />
        Mit Face ID anmelden
      </Button>

      <FieldSeparator>oder mit Passwort</FieldSeparator>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">E-Mail</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username webauthn"
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Passwort</FieldLabel>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </Field>
          {error && <FieldError>{error}</FieldError>}
          <Button type="submit" size="lg" disabled={pending}>
            Anmelden
          </Button>
        </FieldGroup>
      </form>

      <p className="-mt-2 text-center">
        <Link
          href="/passwort-vergessen"
          className="inline-flex min-h-12 items-center text-muted-foreground underline underline-offset-4"
        >
          Passwort vergessen?
        </Link>
      </p>

      <p className="text-center text-muted-foreground">
        Noch kein Konto?{" "}
        <Link
          href={next === "/start" ? "/register" : `/register?next=${encodeURIComponent(next)}`}
          className="inline-flex min-h-12 items-center font-medium text-foreground underline underline-offset-4"
        >
          Konto erstellen
        </Link>
      </p>
    </div>
  )
}
